import {
  queryOptions,
  useQuery,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { apiFetch } from '@/lib/api-fetch.js';

/**
 * Client for the public user directory typeahead (`GET /v1/users/search`, served by
 * `cti-identity-service`, routed through the gateway). Any authenticated user may look up colleagues
 * by name or email — it does NOT require platform admin. The response is a minimal directory row
 * (`{ id, name, email }`, `name` may be null); nothing sensitive. Used by the workspace Access tab's
 * people picker.
 */

const API_BASE = '/v1/users';

/** A directory row as the SPA consumes it. `name` may be null (Entra hasn't supplied it yet). */
export interface DirectoryUser {
  id: string;
  name: string | null;
  email: string;
}

/** Content type only — `apiFetch` attaches the bearer from the renewal core. */
// eslint-disable-next-line @typescript-eslint/require-await
async function headers(): Promise<Record<string, string>> {
  return { 'Content-Type': 'application/json' };
}

/**
 * Search the directory. A blank term short-circuits to `[]` (the server does the same, but skipping
 * the round-trip keeps the typeahead quiet until the user types). `limit` defaults to 20.
 */
async function searchUsers(term: string, limit = 20): Promise<DirectoryUser[]> {
  const q = term.trim();
  if (!q) return [];
  const params = new URLSearchParams({ q });
  params.set('limit', String(limit));
  const res = await apiFetch(`${API_BASE}/search?${params.toString()}`, {
    headers: await headers(),
  });
  if (!res.ok) throw new Error(`Failed to search users (${res.status})`);
  return (await res.json()) as DirectoryUser[];
}

/** Query-key factory so the typeahead cache is keyed by the exact term + limit. */
export const userKeys = {
  search: (term: string, limit: number) =>
    ['users', 'search', term.trim(), limit] as const,
};

/** TanStack Query factory for one directory search; disabled while the term is blank. */
export function usersSearchQueryOptions(term: string, limit = 20) {
  return queryOptions({
    queryKey: userKeys.search(term, limit),
    queryFn: () => searchUsers(term, limit),
    enabled: term.trim().length > 0,
    staleTime: 30_000,
  });
}

/**
 * Debounced directory search hook for a typeahead input. Debounces the raw term by `delayMs` before
 * it reaches the query so we don't fire a request per keystroke, then runs the search query on the
 * settled value.
 */
export function useUserSearch(
  term: string,
  limit = 20,
  delayMs = 250,
): UseQueryResult<DirectoryUser[], Error> {
  const [debounced, setDebounced] = useState(term);
  useEffect(() => {
    const handle = setTimeout(() => setDebounced(term), delayMs);
    return () => clearTimeout(handle);
  }, [term, delayMs]);
  return useQuery(usersSearchQueryOptions(debounced, limit));
}
